import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Loader2, PhoneCall, ShieldAlert, ShieldCheck, UserCheck, FileCheck2, ArrowRight, ArrowDown } from "lucide-react";
import { track } from "../../lib/analytics";

const TRANSCRIPT = [
  { who: "Agent", text: "Thanks for calling — how can I help today?" },
  { who: "Customer", text: "I'm calling about my payments. Things have been really difficult since I lost my husband recently.", flag: true },
  { who: "Agent", text: "I'm very sorry to hear that. Let's look at your account together." },
];

const STAGES = [
  { key: "risk", label: "Risk indicator", icon: ShieldAlert, tone: "amber", body: "Potential customer vulnerability" },
  { key: "policy", label: "Policy", icon: ShieldCheck, tone: "cyan", body: "Enhanced support procedure" },
  { key: "action", label: "Action", icon: UserCheck, tone: "cyan", body: "Route to human review" },
  { key: "reviewer", label: "Reviewer", icon: UserCheck, tone: "cyan", body: "Confirms / dismisses / escalates" },
  { key: "evidence", label: "Evidence", icon: FileCheck2, tone: "green", body: "Decision recorded" },
];

const toneText = { amber: "text-signal-amber", cyan: "text-primary", green: "text-signal-green" };
const toneBorder = { amber: "border-signal-amber/40 bg-signal-amber/[0.06]", cyan: "border-primary/40 bg-primary/[0.06]", green: "border-signal-green/40 bg-signal-green/[0.06]" };

const DASH = [
  { label: "Interactions screened", value: "48,210" },
  { label: "Requiring review", value: "1,340" },
  { label: "Potential vulnerability", value: "212" },
  { label: "Advice-boundary flags", value: "168" },
  { label: "Review outcomes logged", value: "1,205" },
  { label: "Escalations", value: "97" },
];

export default function LogbergDemo() {
  const [running, setRunning] = useState(false);
  const [revealed, setRevealed] = useState(0);

  const run = () => {
    setRunning(true); setRevealed(0);
    track("fs_logberg_screen", {});
    let i = 0;
    const tick = () => { i += 1; setRevealed(i); if (i >= STAGES.length) { setRunning(false); return; } setTimeout(tick, 450); };
    setTimeout(tick, 300);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="fs-logberg-demo">
      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-5 sm:p-6">
        <div className="flex items-center gap-2.5 border-b border-white/10 pb-3">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary"><PhoneCall className="h-4 w-4" /></span>
          <p className="text-sm font-semibold text-foreground">Call transcript</p>
          <span className="ml-auto text-[0.7rem] text-muted-foreground/70">Synthetic</span>
        </div>
        <div className="mt-4 space-y-2.5">
          {TRANSCRIPT.map((t, i) => (
            <div key={i} className={`rounded-xl border px-4 py-3 ${t.flag ? "border-signal-amber/40 bg-signal-amber/[0.06]" : "border-white/10 bg-navy-800/50"}`}>
              <p className={`text-[0.65rem] font-semibold uppercase tracking-wide ${t.flag ? "text-signal-amber" : "text-muted-foreground/70"}`}>{t.who}{t.flag && " · flagged"}</p>
              <p className="mt-1 text-sm text-foreground/90">{t.text}</p>
            </div>
          ))}
        </div>
        <button onClick={run} disabled={running} data-testid="fs-logberg-run" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-70">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Screen this call
        </button>
        <div className="mt-4 space-y-2">
          {STAGES.map((s, i) => {
            const SIcon = s.icon;
            const shown = revealed > i;
            return (
              <div key={s.key}>
                <div className={`flex items-center gap-3 rounded-lg border px-3.5 py-2.5 transition-all duration-300 ${shown ? toneBorder[s.tone] + " opacity-100" : "border-white/8 bg-navy-800/40 opacity-45"}`}>
                  <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/[0.04] ${shown ? toneText[s.tone] : "text-muted-foreground/50"}`}><SIcon className="h-3.5 w-3.5" /></span>
                  <span className="flex-1">
                    <span className={`block text-[0.65rem] font-semibold uppercase tracking-wide ${shown ? toneText[s.tone] : "text-muted-foreground/60"}`}>{s.label}</span>
                    <span className="block text-sm text-foreground/85">{shown ? s.body : "—"}</span>
                  </span>
                </div>
                {i < STAGES.length - 1 && <div className="flex justify-center py-0.5"><ArrowDown className="h-3 w-3 text-muted-foreground/40" /></div>}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-muted-foreground/70">norse3 uses "potential vulnerability indicator" language. Automated screening supports — it does not replace — manual QA.</p>
      </div>

      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-5 sm:p-6">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <p className="text-sm font-semibold text-foreground">Management information</p>
          <span className="text-[0.7rem] text-muted-foreground/70">Synthetic demonstration data</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2.5">
          {DASH.map((d) => (
            <div key={d.label} className="rounded-xl border border-white/10 bg-navy-800/50 p-4">
              <p className="text-2xl font-bold text-foreground">{d.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{d.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl border border-white/10 bg-navy-800/50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">Recurring themes</p>
          <div className="mt-3 space-y-2">
            {[["Potential vulnerability", 62], ["Advice-boundary language", 48], ["Complaint indicators", 31], ["Sensitive information", 24]].map(([t, pct]) => (
              <div key={t}>
                <div className="flex justify-between text-xs text-foreground/85"><span>{t}</span><span>{pct}%</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-white/10"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${pct}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
        <Link to="/book-a-demo" state={{ message: "Interested in discussing Lögberg for financial services." }} data-testid="fs-logberg-cta" className="mt-4 inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm text-foreground hover:bg-white/5">
          Discuss Lögberg <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
